import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "./axios";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { companiesKeys } from "./companies";
import { getCurrentCompanie } from "../utils/currentCompanie";

export const userKeys = {
  currentUser: () => ["currentUser"] as const,
  all: (
    page: number,
    per_page: number,
    filter: string,
    company: string,
    role: string | null,
    status: string | null
  ) =>
    [
      "user",
      "page",
      page,
      "per_page",
      per_page,
      "filter",
      filter,
      "company",
      company,
      "role",
      role,
      "status",
      status,
    ] as const,
};

export interface RequestLogin {
  email: string;
  password: string;
}

export const useUserLogin = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return useMutation({
    mutationKey: userKeys.currentUser(),
    mutationFn: (newUser: RequestLogin) => {
      return axios.post("/user/login", JSON.stringify(newUser));
    },
    onSuccess: (data) => {
      const { data: user } = data;
      if (user?.is_super_admin === 1) {
        localStorage.setItem("currentUser", JSON.stringify(user));
        // SECURITY (VULN-02) : l'API ne renvoie plus la liste des sessions.
        // Le jeton de la session courante est a la racine : user.token.
        localStorage.setItem("token", user?.token ?? user?.sessions?.[0]?.token);
        toast.success(t("login.success", "Authentication successful!"));
        window?.location?.reload();
        navigate("/dashboard");
      } else {
        toast.error(t("login.accessDenied", "Access denied. Reserved to super-admins"));
      }
    },
    onError: () => {
      toast.error(t("login.error", "Identifier error"));
    },
  });
};

interface ResponseGetUsers {
  data: {
    users: {
      data: TUser[];
      total: number;
    };
  };
}

export const useGetAllUsers = (
  page: number = 1,
  per_page: number = 10,
  filter: string = "",
  company: string = "",
  role: string | null = null,
  status: string | null = null
) => {
  const { data, error, ...res } = useQuery({
    queryKey: userKeys.all(page, per_page, filter, company, role, status),
    queryFn: async (): Promise<ResponseGetUsers> =>
      axios.get(`/admin/users`, {
        params: {
          page,
          per_page,
          searchBar: filter,
          company: company.trim().length > 0 ? company : undefined,
          status: status && status?.trim().length > 0 ? status : undefined,
          role,
        },
      }),
    staleTime: 0,
  });

  if (error) {
  }
  return {
    users: data?.data?.users?.data ?? [],
    totalCount: data?.data?.users?.total,
    ...res,
  };
};

export const useDeleteUserAdmin = (
  onClose?: () => void,
  initUserAdmin?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      /*
       * `id` here is a `user`.id and nothing else.
       *
       * Callers used to pass the list row's `userID`, which the API was
       * populating from `company_user.id` - the MEMBERSHIP key. The two id
       * spaces mostly do not overlap, which is why this showed up as
       * "User not found" on a row that was visibly on screen; where they DO
       * overlap it silently deleted the wrong person. Never send a membership
       * id to a /admin/users/{id} route.
       */
      if (!Number.isInteger(id) || id <= 0) {
        throw new Error("invalid user id");
      }
      const data = await axios.delete(`/admin/users/${id}`);
      return data.data;
    },
    onSuccess: (_) => {
      onClose && onClose();
      initUserAdmin && initUserAdmin();

      queryClient.invalidateQueries({ queryKey: ["user"] } as any);

      const companieId = getCurrentCompanie();
      if (companieId) {
        const keysCompanie = companiesKeys.companieEmployee(companieId);
        queryClient.invalidateQueries(keysCompanie as any);
      }

      toast.success(t("user.adminDeleteSuccess", "Change saved"));
    },
    onError: (err: any) => {
      // The message is not guaranteed: a network failure, a timeout or an HTML
      // error page all leave it undefined, and `toast.error(undefined)`
      // renders an empty box - the operator sees nothing happen at all.
      toast.error(
        err?.response?.data?.message ??
          t("user.adminDeleteError", "Deletion failed")
      );
    },
  });
};

interface RequestUpdateUserAdmin {
  /** A `user`.id. NOT a `company_user`.id - see useDeleteUserAdmin. */
  idUser?: number;
  firstname: string;
  lastname: string;
  email: string;
  is_active: number;
}

export const useUpdateUserAdmin = (
  onClose?: () => void,
  initUserAdmin?: () => void
) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newUserAdmin: RequestUpdateUserAdmin) => {
      const { idUser, ...payload } = newUserAdmin;

      if (!Number.isInteger(idUser) || (idUser as number) <= 0) {
        throw new Error("invalid user id");
      }

      /*
       * BODY, not query string.
       *
       * This used to be a template-interpolated URL:
       *
       *   `/admin/users/${idUser}?firstname=${firstname}&lastname=${...}`
       *
       * Three separate problems with that, all of them live:
       *
       *  1. NO ENCODING. A name or address containing `&`, `+`, `#` or `?`
       *     truncated or corrupted every field after it. "Marie & Paul" wrote
       *     `firstname=Marie ` and invented a `Paul` parameter. Silent data
       *     corruption, not an error.
       *  2. PII IN THE URL. Full name and email landed in the browser history,
       *     nginx access logs, any proxy in between, and the `Referer` header
       *     of the next request. A request body goes in none of those.
       *  3. It made the mutation indistinguishable from a GET-shaped call and
       *     hid the fact that PUT was sending an empty body.
       *
       * axios serialises the object and encodes it correctly; Lumen reads it
       * through $request->all() exactly as before, so the API contract is
       * unchanged.
       */
      const data = await axios.put(`/admin/users/${idUser}`, payload);
      return data.data;
    },
    onSuccess: () => {
      onClose && onClose();
      initUserAdmin && initUserAdmin();

      queryClient.invalidateQueries({ queryKey: ["user"] } as any);

      const companieId = getCurrentCompanie();
      if (companieId) {
        const keysCompanie = companiesKeys.companieEmployee(companieId);
        queryClient.invalidateQueries(keysCompanie as any);
      }

      toast.success(t("user.adminUpdateSuccess", "Change saved"));
    },
    onError: (err: any) => {
      /*
       * Prefer the field-level validation errors over the summary.
       *
       * The API answers 400 with `{message, errors: {email: [...]}}`. Showing
       * only `message` reduced "this email is already used by another account"
       * to Lumen's generic "The given data was invalid.", which tells the
       * operator that something is wrong but not what.
       */
      const fieldErrors = err?.response?.data?.errors;
      const firstFieldError =
        fieldErrors && typeof fieldErrors === "object"
          ? (Object.values(fieldErrors).flat() as string[])[0]
          : undefined;

      toast.error(
        firstFieldError ??
          err?.response?.data?.message ??
          t("user.adminUpdateError", "Save failed")
      );
    },
  });
};
